import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getDirectory, listDirectory, errorText } from './apiDirectory';
import { collectionLabel, singularLabel, termLabel, COLLECTIONS } from './directoryHelpers';
import DirectorySettings from './DirectorySettings';
import { useDomain } from '../DomainContext';
import PageHeader from '../ui/PageHeader';
import EmptyState from '../ui/EmptyState';
import Alert from '../ui/Alert';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import ThinBadge from '../seo/ThinBadge';
import SiteSectionNav from '../ui/SiteSectionNav';

const PAGE_SIZE = 50;

const editPath = (domainId, collection, slug) =>
  `/domain/${encodeURIComponent(domainId)}/directory/${collection}/${encodeURIComponent(slug)}/edit`;

const StatusBadge = ({ status }) => (
  <Badge state={(status || 'active') === 'active' ? 'success' : 'draft'}>{status || 'active'}</Badge>
);

/* One table per collection; columns depend on what the collection holds. */
const columns = (collection, vocab) => {
  if (collection === 'listings') {
    return [
      ['Name', (it) => (
        <>
          <strong>{it.name}</strong>
          {it.thin?.thin && <div style={{ marginTop: 4 }}><ThinBadge verdict={it.thin} /></div>}
        </>
      )],
      ['City', (it) => it.city || <span className="muted">—</span>],
      ['Kind', (it) => (it.kind ? termLabel(vocab, 'kinds', it.kind) : <span className="muted">—</span>)],
      ['Claimed', (it) => (it.claimed ? <Badge state="info">Claimed</Badge> : <span className="muted">—</span>)],
      ['Featured', (it) => (it.featured ? <Badge state="warn">Featured</Badge> : <span className="muted">—</span>)],
      ['Status', (it) => <StatusBadge status={it.status} />],
    ];
  }
  if (collection === 'people') {
    return [
      ['Name', (it) => <strong>{it.name}</strong>],
      ['Title', (it) => it.title || <span className="muted">—</span>],
      [collectionLabel(vocab, 'listings'), (it) => ((it.listing_slugs || []).length
        ? <span className="mono small">{it.listing_slugs[0]}{it.listing_slugs.length > 1 ? ` +${it.listing_slugs.length - 1}` : ''}</span>
        : <span className="muted">—</span>)],
      ['Status', (it) => <StatusBadge status={it.status} />],
    ];
  }
  return [
    ['Name', (it) => <strong>{it.name}</strong>],
    ['Title', (it) => it.title || <span className="muted">—</span>],
    [singularLabel(vocab, 'people'), (it) => (it.person_slug ? <span className="mono small">{it.person_slug}</span> : <span className="muted">—</span>)],
    ['Status', (it) => <StatusBadge status={it.status} />],
  ];
};

export default function DomainDirectory() {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { domains, selectedDomain } = useDomain();
  const tab = [...COLLECTIONS, 'settings'].includes(params.get('tab')) ? params.get('tab') : 'listings';

  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [city, setCity] = useState('');
  const [thinOnly, setThinOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  const domain = selectedDomain?.id === domainId ? selectedDomain : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.name || domainId;
  const vocab = cfg?.vocab || null;

  useEffect(() => {
    getDirectory(domainId).then(setCfg).catch((err) => {
      if (err.response?.status === 401) navigate('/login');
      else setCfgError(errorText(err, 'Failed to load the directory'));
    });
  }, [domainId, navigate]);

  const fetchPage = useCallback(async (fromCursor) => {
    if (tab === 'settings') return;
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await listDirectory(domainId, tab, {
        q: q.trim(), status, citySlug: tab === 'listings' ? city.trim() : '', thin: tab === 'listings' && thinOnly,
        limit: PAGE_SIZE, cursor: fromCursor,
      });
      if (id !== reqId.current) return;
      setItems((prev) => (fromCursor ? [...prev, ...(data.items || [])] : (data.items || [])));
      setCursor(data.next_cursor || null);
      setTotal(data.total ?? null);
    } catch (err) {
      if (id !== reqId.current) return;
      if (err.response?.status === 401) navigate('/login');
      else setError(errorText(err, 'Failed to load'));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [domainId, tab, q, status, city, thinOnly, navigate]);

  // Refetch from the start when the tab or a filter changes (debounced for typing).
  useEffect(() => {
    setItems([]);
    setCursor(null);
    const t = setTimeout(() => fetchPage(null), 250);
    return () => clearTimeout(t);
  }, [fetchPage]);

  const setTab = (key) => {
    setQ('');
    setStatus('');
    setCity('');
    setThinOnly(false);
    setParams({ tab: key }, { replace: true });
  };

  if (cfgError) {
    return <div className="page"><Alert type="error">{cfgError}</Alert></div>;
  }
  if (!cfg) {
    return <div className="page"><Spinner label="Loading directory…" /></div>;
  }

  const counts = cfg.counts || {};
  const tabs = cfg.enabled
    ? [...COLLECTIONS.map((c) => [c, `${collectionLabel(vocab, c)}${counts[c] != null ? ` (${counts[c]})` : ''}`]), ['settings', 'Settings']]
    : [['settings', 'Settings']];
  const activeTab = cfg.enabled ? tab : 'settings';
  const newPath = activeTab !== 'settings' ? editPath(domainId, activeTab, 'new') : null;
  const cols = activeTab !== 'settings' ? columns(activeTab, vocab) : [];

  return (
    <div className="page">
      <PageHeader
        title={`Directory · ${hostname}`}
        subtitle={cfg.enabled ? (cfg.metro || 'Directory enabled') : 'Directory is off for this site'}
        actions={(
          <>
            <Link to={`/domain/${domainId}`} className="btn btn-secondary">← Back to domain</Link>
            {newPath && <Link to={newPath} className="btn btn-primary">+ New {singularLabel(vocab, activeTab).toLowerCase()}</Link>}
          </>
        )}
      />
      <SiteSectionNav section="settings" />

      <div className="chip-group" role="tablist" aria-label="Directory sections" style={{ marginBottom: 16 }}>
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={activeTab === key}
                  className={`chip${activeTab === key ? ' active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'settings' ? (
        <div className="card" role="tabpanel">
          {!cfg.enabled && (
            <p className="muted" style={{ marginTop: 0 }}>
              Enable the directory and give it a vocab to manage {collectionLabel(vocab, 'listings').toLowerCase()}, {collectionLabel(vocab, 'people').toLowerCase()} and authors here.
            </p>
          )}
          <DirectorySettings domainId={domainId} onSaved={(d) => setCfg(d)} />
        </div>
      ) : (
        <div role="tabpanel">
          <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
          <div className="toolbar">
            <input type="search" className="input grow" placeholder={`Search ${collectionLabel(vocab, activeTab).toLowerCase()} by name…`}
                   aria-label="Search by name" value={q} onChange={(e) => setQ(e.target.value)} />
            {activeTab === 'listings' && (
              <input type="text" className="input" style={{ width: 140 }} placeholder="city slug" aria-label="Filter by city slug"
                     value={city} onChange={(e) => setCity(e.target.value)} />
            )}
            <select className="select" style={{ width: 120 }} aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="hidden">Hidden</option>
            </select>
            {activeTab === 'listings' && (
              <button type="button" className={`chip${thinOnly ? ' active' : ''}`} aria-pressed={thinOnly}
                      onClick={() => setThinOnly((v) => !v)}
                      title="Thin listings are marked noindex and left out of the sitemap until claimed or filled in. Rules: Settings → Site settings → SEO: thin pages.">
                Thin only
              </button>
            )}
            <span className="muted small">
              {items.length}{total != null ? ` of ${total}` : ''}
              {activeTab === 'listings' && !thinOnly && items.length > 0 && ` · ${items.filter((it) => it.thin?.thin).length} thin of ${items.length} loaded`}
              {thinOnly && ' thin'}
            </span>
          </div>

          {items.length === 0 && loading ? (
            <Spinner label="Loading…" />
          ) : items.length === 0 ? (
            <EmptyState
              title={q || status || city || thinOnly ? 'No matches' : `No ${collectionLabel(vocab, activeTab).toLowerCase()} yet`}
              message={q || status || city || thinOnly ? 'Nothing matches those filters.' : undefined}
              action={newPath && !(q || status || city || thinOnly) ? <Link to={newPath} className="btn btn-primary">+ New {singularLabel(vocab, activeTab).toLowerCase()}</Link> : undefined}
            />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>{cols.map(([h]) => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.slug} className="clickable" onClick={() => navigate(editPath(domainId, activeTab, it.slug))}>
                        {cols.map(([h, render]) => <td key={h}>{render(it)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {cursor && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => fetchPage(cursor)}>
                    {loading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
